import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { AnalyticsManager } from '@/components/analytics/AnalyticsManager';
import { CookieConsent } from '@/components/privacy/CookieConsent';
import { ConsentProvider } from '@/contexts/ConsentContext';
import { ServiceWorkerUpdate } from '@/components/ServiceWorkerUpdate';
import './index.css';

const Home = lazy(() => import('@/pages/Home').then((m) => ({ default: m.Home })));
const Events = lazy(() => import('@/pages/Events').then((m) => ({ default: m.Events })));
const EventDetail = lazy(() => import('@/pages/EventDetail').then((m) => ({ default: m.EventDetail })));
const EventSeriesPage = lazy(() => import('@/pages/EventSeries').then((m) => ({ default: m.EventSeriesPage })));
const LegacyVenueRedirect = lazy(() => import('@/pages/LegacyVenueRedirect').then((m) => ({ default: m.LegacyVenueRedirect })));
const LegacyCategoryRedirect = lazy(() => import('@/pages/LegacyCategoryRedirect').then((m) => ({ default: m.LegacyCategoryRedirect })));
const LegacyEventRedirect = lazy(() => import('@/pages/LegacyEventRedirect').then((m) => ({ default: m.LegacyEventRedirect })));
const Blog = lazy(() => import('@/pages/Blog').then((m) => ({ default: m.Blog })));
const BlogPost = lazy(() => import('@/pages/BlogPost').then((m) => ({ default: m.BlogPost })));
const Faq = lazy(() => import('@/pages/Faq').then((m) => ({ default: m.Faq })));
const Page = lazy(() => import('@/pages/Page').then((m) => ({ default: m.Page })));
const DynamicPage = lazy(() => import('@/pages/DynamicPage').then((m) => ({ default: m.DynamicPage })));
const Radio = lazy(() => import('@/pages/Radio').then((m) => ({ default: m.Radio })));
const Playlist = lazy(() => import('@/pages/Playlist').then((m) => ({ default: m.Playlist })));
const Contact = lazy(() => import('@/pages/Contact').then((m) => ({ default: m.Contact })));
const Contest = lazy(() => import('@/pages/Contest').then((m) => ({ default: m.Contest })));
const Newsletter = lazy(() => import('@/pages/Newsletter').then((m) => ({ default: m.Newsletter })));
const MemberLogin = lazy(() => import('@/pages/MemberLogin').then((m) => ({ default: m.MemberLogin })));
const MemberSignup = lazy(() => import('@/pages/MemberSignup').then((m) => ({ default: m.MemberSignup })));
const MemberVerify = lazy(() => import('@/pages/MemberVerify').then((m) => ({ default: m.MemberVerify })));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPassword })));
const Privacy = lazy(() => import('@/pages/Privacy').then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import('@/pages/Terms').then((m) => ({ default: m.Terms })));

const OwnerLayout = lazy(() => import('@/owner/OwnerLayout').then((m) => ({ default: m.OwnerLayout })));
const OwnerDashboard = lazy(() => import('@/owner/OwnerDashboard').then((m) => ({ default: m.OwnerDashboard })));
const MemberLayout = lazy(() => import('@/member/MemberLayout').then((m) => ({ default: m.MemberLayout })));
const MemberDashboard = lazy(() => import('@/member/MemberDashboard').then((m) => ({ default: m.MemberDashboard })));
const MemberProfile = lazy(() => import('@/member/MemberProfile').then((m) => ({ default: m.MemberProfile })));

const AdminLogin = lazy(() => import('@/admin/Login').then((m) => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import('@/admin/Dashboard').then((m) => ({ default: m.AdminDashboard })));
const AdminLayout = lazy(() => import('@/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AnalyticsDashboard = lazy(() => import('@/admin/Analytics/AnalyticsDashboard').then((m) => ({ default: m.AnalyticsDashboard })));
const PostsManager = lazy(() => import('@/admin/Posts/PostsManager').then((m) => ({ default: m.PostsManager })));
const MediaLibraryManager = lazy(() => import('@/admin/Media/MediaLibraryManager').then((m) => ({ default: m.MediaLibraryManager })));
const PagesManager = lazy(() => import('@/admin/pages/PagesManager').then((m) => ({ default: m.PagesManager })));
const LeadsManager = lazy(() => import('@/admin/Leads/LeadsManager').then((m) => ({ default: m.LeadsManager })));
const ContactMessagesManager = lazy(() =>
  import('@/admin/Messages/ContactMessagesManager').then((m) => ({ default: m.ContactMessagesManager }))
);
const NavManager = lazy(() => import('@/admin/Navigation/NavManager').then((m) => ({ default: m.NavManager })));
const ScheduleManager = lazy(() => import('@/admin/Schedule/ScheduleManager').then((m) => ({ default: m.ScheduleManager })));
const SettingsManager = lazy(() => import('@/admin/settings/SettingsManager').then((m) => ({ default: m.SettingsManager })));
const HomepageManager = lazy(() => import('@/admin/Homepage/HomepageManager').then((m) => ({ default: m.HomepageManager })));
const BusinessDirectoryManager = lazy(() =>
  import('@/admin/Directory/BusinessDirectoryManager').then((m) => ({ default: m.BusinessDirectoryManager }))
);
const ShowsManager = lazy(() => import('@/admin/Shows/ShowsManager').then((m) => ({ default: m.ShowsManager })));
const UsersManager = lazy(() => import('@/admin/Users/UsersManager').then((m) => ({ default: m.UsersManager })));
const GalleriesManager = lazy(() => import('@/admin/Galleries/GalleriesManager').then((m) => ({ default: m.GalleriesManager })));
const ContestsManager = lazy(() => import('@/admin/Contests/ContestsManager').then((m) => ({ default: m.ContestsManager })));
const EventSubmissionsManager = lazy(() =>
  import('@/admin/EventSubmissions/EventSubmissionsManager').then((m) => ({ default: m.EventSubmissionsManager }))
);
const MembersManager = lazy(() => import('@/admin/Members/MembersManager').then((m) => ({ default: m.MembersManager })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function App() {
  return (
    <ConsentProvider>
      <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
        <AnalyticsManager />
        <CookieConsent />
        <ServiceWorkerUpdate />
        <Suspense fallback={<div className="p-6 text-center text-muted-foreground">Loading...</div>}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            {/* Legacy event URL redirect - must come before /events routes */}
            <Route path="event/:slug" element={<LegacyEventRedirect />} />
            <Route path="events" element={<Events />} />
            <Route path="events/venues/:slug" element={<LegacyVenueRedirect />} />
            <Route path="events/category/:category/day/:day" element={<LegacyCategoryRedirect />} />
            <Route path="events/category/:category/week/:week" element={<LegacyCategoryRedirect />} />
            <Route path="events/series/:slug" element={<EventSeriesPage />} />
            <Route path="events/:slug" element={<EventDetail />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogPost />} />
            <Route path="faq" element={<Faq />} />
            <Route path="photos" element={<Navigate to="/" replace />} />
            <Route path="photos/:slug" element={<Navigate to="/" replace />} />
            <Route path="radio" element={<Radio />} />
            <Route path="playlist" element={<Playlist />} />
            <Route path="contact" element={<Contact />} />
            <Route path="contest" element={<Contest />} />
            <Route path="contests" element={<Contest />} />
            <Route path="newsletter" element={<Newsletter />} />
            <Route path="feedback" element={<Navigate to="/contact" replace />} />
            <Route path="directory/submit" element={<Navigate to="/" replace />} />
            <Route path="directory/:slug" element={<Navigate to="/" replace />} />
            <Route path="directory" element={<Navigate to="/" replace />} />
            {/* Legacy page route */}
            <Route path="page/:slug" element={<Page />} />
            {/* Dynamic page route - must be last */}
            <Route path=":slug" element={<DynamicPage />} />
          </Route>

          <Route path="/owner" element={<OwnerLayout />}>
            <Route index element={<OwnerDashboard />} />
          </Route>

          {/* Member Routes */}
          <Route path="/members" element={<MemberSignup />} />
          <Route path="/member/login" element={<MemberLogin />} />
          <Route path="/member/signup" element={<MemberSignup />} />
          <Route path="/member/verify" element={<MemberVerify />} />
          <Route path="/member/forgot-password" element={<ForgotPassword />} />
          <Route path="/member/reset-password" element={<ResetPassword />} />
          <Route path="/member" element={<MemberLayout />}>
            <Route index element={<MemberDashboard />} />
            <Route path="submit-event" element={<Navigate to="/member" replace />} />
            <Route path="my-events" element={<Navigate to="/member" replace />} />
            <Route path="profile" element={<MemberProfile />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="homepage" element={<HomepageManager />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="posts" element={<PostsManager />} />
            <Route path="media" element={<MediaLibraryManager />} />
            <Route path="pages" element={<PagesManager />} />
            <Route path="leads" element={<LeadsManager />} />
            <Route path="messages" element={<ContactMessagesManager />} />
            <Route path="navigation" element={<NavManager />} />
            <Route path="schedule" element={<ScheduleManager />} />
            <Route path="shows" element={<ShowsManager />} />
            <Route path="directory" element={<BusinessDirectoryManager />} />
            <Route path="settings" element={<SettingsManager />} />
            <Route path="users" element={<UsersManager />} />
            <Route path="galleries" element={<GalleriesManager />} />
            <Route path="contests" element={<ContestsManager />} />
            <Route path="event-submissions" element={<EventSubmissionsManager />} />
            <Route path="members" element={<MembersManager />} />
          </Route>
        </Routes>
        </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
      </HelmetProvider>
    </ConsentProvider>
  );
}

export default App;
